<script setup lang="ts">
import { computed, type Component } from "vue";

import CardHeading from "./CardHeading.vue";

/**
 * The card grammar the create wizard, the edit wizard and the project settings page all share: a
 * tinted title bar carrying an icon, a heading and a caption, with the fields below it on white.
 *
 * It is hand-rolled in eighteen files, which is why every card looks identical — there is nowhere to
 * say "this one matters more". `emphasis` is that place. New cards should use this; the copies are
 * being migrated as their pages are touched.
 *
 * Not a Reusable: the heading derives its level from the wizard-step context (`headingLevel.ts`), and
 * a component in `Reusables/` must not depend on a feature.
 */
const props = withDefaults(
  defineProps<{
    icon: Component;
    /** Omit when the caller needs its own element — see the `heading` slot. */
    heading?: string;
    caption?: string;
    /** `danger` is the settings page's delete card: the one card whose button saves nothing. */
    emphasis?: "default" | "danger";
    /** The body's own vertical rhythm, which differs between a field stack and a row list. */
    bodyClass?: string;
  }>(),
  { heading: "", caption: "", emphasis: "default", bodyClass: "gap-3" }
);

const isDanger = computed(() => props.emphasis === "danger");

// slate-300, not the slate-200 the rest of the app draws element borders in: on the white content
// ground, with ten of these to a page, slate-200 read as no outline at all.
const shellClass = computed(() => (isDanger.value ? "border-app-red-200" : "border-slate-300"));

const bandClass = computed(() => (isDanger.value ? "border-app-red-200 bg-app-red-50" : "border-slate-300 bg-slate-50"));

const tileClass = computed(() => (isDanger.value ? "bg-app-red-100" : "bg-app-blue-50"));

const iconClass = computed(() => (isDanger.value ? "text-app-red-600" : "text-app-blue-600"));
</script>

<template>
  <!-- The band's negative margins cancel the card's own padding so it reaches the edges; they must
       match that padding. `rounded-t-8` rather than `overflow-hidden` on the shell, because several
       of these hold selects whose dropdown renders in flow and would be clipped. -->
  <div :class="['rounded-8 bg-slate-0 p-px-20 flex flex-col border', shellClass, bodyClass]">
    <div :class="['-mx-px-20 -mt-px-20 px-px-20 pt-px-20 rounded-t-8 flex flex-col gap-1 border-b pb-3', bandClass]">
      <div class="flex items-center justify-between gap-2">
        <div class="flex items-center gap-2">
          <span :class="['rounded-4 flex shrink-0 items-center justify-center p-1', tileClass]">
            <component
              :is="icon"
              :class="['icon-sm', iconClass]"
              :stroke-width="1.5"
              aria-hidden="true" />
          </span>
          <!-- CardHeading derives its level from the host, so the same card is an h2 on a wizard
               route and an h3 on the settings page. A card that exists on one page only passes its
               own element through the slot instead. -->
          <slot name="heading">
            <CardHeading class="t-sh4 text-type-default">{{ heading }}</CardHeading>
          </slot>
        </div>
        <slot name="action" />
      </div>

      <!-- Explanations are captions, not tooltips: a hover-only note is invisible to most readers
           and unreachable on touch. -->
      <slot name="caption">
        <p
          v-if="caption"
          class="t-caption text-type-muted max-w-prose">
          {{ caption }}
        </p>
      </slot>
    </div>

    <slot />
  </div>
</template>
